import {
  RCertificateLogoInput,
  RCertificateSignatureInput,
  RCertificateTemplateType,
  RCertificatePropertiesQuery,
  RUpsertCertificatePropertiesMutation,
  RUpsertCertificatePropertiesInput,
} from '@/store/v2';

type CertificatePropertiesPayload = NonNullable<RCertificatePropertiesQuery['certificateProperties']>;
type UpsertCertificatePropertiesPayload = RUpsertCertificatePropertiesMutation['upsertCertificateProperties'];

export type SignatureFieldValues = {
  name: string;
  title: string;
  institution: string;
  signatureFileUrl?: string;
};

export type FooterLogoFieldValues = {
  logoDescription: string;
  logoFileUrl?: string;
};

export const getJourneyCertificateUrn = (journeyId: number): string => `urn:collegial:self:journey:${journeyId}`;

/**
 * Professional certificate fields: journey title, dates, free-text description,
 * up to three signatures and two footer logos (logo image + organisation text).
 */
export type ProfessionalCertificateFormState = {
  templateType: RCertificateTemplateType.Professional;
  title: string;
  dates: string;
  description: string;
  firstSignature: SignatureFieldValues;
  secondSignature: SignatureFieldValues;
  thirdSignature: SignatureFieldValues;
  firstFooterLogo: FooterLogoFieldValues;
  secondFooterLogo: FooterLogoFieldValues;
};

/**
 * Classic certificate fields: a header logo + header text, journey title, dates,
 * up to two signatures and a single footer logo (image only).
 */
export type ClassicCertificateFormState = {
  templateType: RCertificateTemplateType.Classic;
  headerLogoFileUrl?: string;
  header: string;
  title: string;
  dates: string;
  firstSignature: SignatureFieldValues;
  secondSignature: SignatureFieldValues;
  footerLogoFileUrl?: string;
};

export type JourneyCertificateFormState = ProfessionalCertificateFormState | ClassicCertificateFormState;

const createEmptySignature = (): SignatureFieldValues => ({ name: '', title: '', institution: '' });

export const createEmptyProfessionalCertificateFormState = (): ProfessionalCertificateFormState => ({
  templateType: RCertificateTemplateType.Professional,
  title: '',
  dates: '',
  description: '',
  firstSignature: createEmptySignature(),
  secondSignature: createEmptySignature(),
  thirdSignature: createEmptySignature(),
  firstFooterLogo: { logoDescription: '' },
  secondFooterLogo: { logoDescription: '' },
});

const createEmptyClassicCertificateFormState = (): ClassicCertificateFormState => ({
  templateType: RCertificateTemplateType.Classic,
  header: '',
  title: '',
  dates: '',
  firstSignature: createEmptySignature(),
  secondSignature: createEmptySignature(),
});

export const createEmptyCertificateFormState = (
  templateType: RCertificateTemplateType,
  journeyTitle = '',
): JourneyCertificateFormState =>
  templateType === RCertificateTemplateType.Classic
    ? { ...createEmptyClassicCertificateFormState(), title: journeyTitle }
    : { ...createEmptyProfessionalCertificateFormState(), title: journeyTitle };

const mapSignatureToForm = (signature?: CertificatePropertiesPayload['signatures'][number] | null): SignatureFieldValues => ({
  name: signature?.name ?? '',
  title: signature?.title ?? '',
  institution: signature?.institution ?? '',
  signatureFileUrl: signature?.signatureUrl ?? undefined,
});

const mapLogoToForm = (logo?: CertificatePropertiesPayload['logos'][number] | null): FooterLogoFieldValues => ({
  logoDescription: logo?.organisationText ?? '',
  logoFileUrl: logo?.logoUrl ?? undefined,
});

const sortByOrder = <T extends { order: number }>(items: readonly T[]): T[] => [...items].sort((a, b) => a.order - b.order);

const mapProfessionalToFormState = (
  properties: CertificatePropertiesPayload | UpsertCertificatePropertiesPayload,
  journeyTitleFallback: string,
): ProfessionalCertificateFormState => {
  const signatures = sortByOrder(properties.signatures);
  const logos = sortByOrder(properties.logos);

  return {
    templateType: RCertificateTemplateType.Professional,
    title: properties.title ?? journeyTitleFallback,
    dates: properties.date ?? '',
    description: properties.description ?? '',
    firstSignature: mapSignatureToForm(signatures[0]),
    secondSignature: mapSignatureToForm(signatures[1]),
    thirdSignature: mapSignatureToForm(signatures[2]),
    firstFooterLogo: mapLogoToForm(logos[0]),
    secondFooterLogo: mapLogoToForm(logos[1]),
  };
};

const mapClassicToFormState = (
  properties: CertificatePropertiesPayload | UpsertCertificatePropertiesPayload,
  journeyTitleFallback: string,
): ClassicCertificateFormState => {
  const signatures = sortByOrder(properties.signatures);
  // Classic logos are keyed by order: 1 = header logo, 2 = footer logo.
  const headerLogo = properties.logos.find((logo) => logo.order === 1);
  const footerLogo = properties.logos.find((logo) => logo.order === 2);

  return {
    templateType: RCertificateTemplateType.Classic,
    header: properties.header ?? '',
    title: properties.title ?? journeyTitleFallback,
    dates: properties.date ?? '',
    firstSignature: mapSignatureToForm(signatures[0]),
    secondSignature: mapSignatureToForm(signatures[1]),
    headerLogoFileUrl: headerLogo?.logoUrl ?? undefined,
    footerLogoFileUrl: footerLogo?.logoUrl ?? undefined,
  };
};

export const mapCertificatePropertiesToFormState = (
  properties: CertificatePropertiesPayload | UpsertCertificatePropertiesPayload,
  journeyTitleFallback: string,
): JourneyCertificateFormState =>
  properties.templateType === RCertificateTemplateType.Classic
    ? mapClassicToFormState(properties, journeyTitleFallback)
    : mapProfessionalToFormState(properties, journeyTitleFallback);

const hasSignatureContent = (signature: SignatureFieldValues): boolean =>
  Boolean(signature.name.trim() || signature.title.trim() || signature.institution.trim() || signature.signatureFileUrl);

const hasFooterLogoContent = (logo: FooterLogoFieldValues): boolean => Boolean(logo.logoDescription.trim() || logo.logoFileUrl);

type AssetUrlResolver = (url: string | undefined) => Promise<string | undefined>;

const buildSignatureInputs = async (
  entries: Array<{ order: number; values: SignatureFieldValues }>,
  resolveAssetUrl: AssetUrlResolver,
): Promise<RCertificateSignatureInput[]> => {
  const signatures: RCertificateSignatureInput[] = [];

  for (const { order, values } of entries) {
    if (!hasSignatureContent(values)) {
      continue;
    }

    signatures.push({
      order,
      name: values.name.trim() || undefined,
      title: values.title.trim() || undefined,
      institution: values.institution.trim() || undefined,
      signatureUrl: await resolveAssetUrl(values.signatureFileUrl),
    });
  }

  return signatures;
};

const buildProfessionalInput = async (
  urn: string,
  formState: ProfessionalCertificateFormState,
  resolveAssetUrl: AssetUrlResolver,
): Promise<RUpsertCertificatePropertiesInput> => {
  const signatures = await buildSignatureInputs(
    [
      { order: 1, values: formState.firstSignature },
      { order: 2, values: formState.secondSignature },
      { order: 3, values: formState.thirdSignature },
    ],
    resolveAssetUrl,
  );

  const logoEntries: Array<{ order: number; values: FooterLogoFieldValues }> = [
    { order: 1, values: formState.firstFooterLogo },
    { order: 2, values: formState.secondFooterLogo },
  ];

  const logos: RCertificateLogoInput[] = [];

  for (const { order, values } of logoEntries) {
    if (!hasFooterLogoContent(values)) {
      continue;
    }

    logos.push({
      order,
      organisationText: values.logoDescription.trim() || undefined,
      logoUrl: await resolveAssetUrl(values.logoFileUrl),
    });
  }

  return {
    urn,
    templateType: RCertificateTemplateType.Professional,
    title: formState.title.trim() || undefined,
    description: formState.description.trim() || undefined,
    date: formState.dates.trim() || undefined,
    signatures,
    logos,
  };
};

const buildClassicInput = async (
  urn: string,
  formState: ClassicCertificateFormState,
  resolveAssetUrl: AssetUrlResolver,
): Promise<RUpsertCertificatePropertiesInput> => {
  const signatures = await buildSignatureInputs(
    [
      { order: 1, values: formState.firstSignature },
      { order: 2, values: formState.secondSignature },
    ],
    resolveAssetUrl,
  );

  // Classic logos are keyed by order: 1 = header logo, 2 = footer logo.
  const logos: RCertificateLogoInput[] = [];

  if (formState.headerLogoFileUrl) {
    logos.push({ order: 1, logoUrl: await resolveAssetUrl(formState.headerLogoFileUrl) });
  }

  if (formState.footerLogoFileUrl) {
    logos.push({ order: 2, logoUrl: await resolveAssetUrl(formState.footerLogoFileUrl) });
  }

  return {
    urn,
    templateType: RCertificateTemplateType.Classic,
    title: formState.title.trim() || undefined,
    date: formState.dates.trim() || undefined,
    header: formState.header.trim() || undefined,
    signatures,
    logos,
  };
};

type BuildCertificateMutationInputParams = {
  urn: string;
  formState: JourneyCertificateFormState;
  resolveAssetUrl: AssetUrlResolver;
};

export const buildUpsertCertificatePropertiesInput = async ({
  urn,
  formState,
  resolveAssetUrl,
}: BuildCertificateMutationInputParams): Promise<RUpsertCertificatePropertiesInput> =>
  formState.templateType === RCertificateTemplateType.Classic
    ? buildClassicInput(urn, formState, resolveAssetUrl)
    : buildProfessionalInput(urn, formState, resolveAssetUrl);
